import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { Building2, Users, Monitor, UserCog, Activity, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getToken();
      
      // Fetch both dashboard stats and detailed statistics
      const [dashboardResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(dashboardResponse.data);
      setDetailedStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SuperAdminLayout><div style={{ padding: '2rem' }}>Laster...</div></SuperAdminLayout>;
  if (!stats) return <SuperAdminLayout><div style={{ padding: '2rem' }}>Kunne ikke laste statistikk</div></SuperAdminLayout>;

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.dashboard.title')}</h1>
      </div>

      {/* System Overview Stats */}
      <div className="admin-stats">
        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><Building2 size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#50E3C2' }} />{t('superAdmin.dashboard.totalCustomers')}</h3>
          <div className="value" style={{ color: '#ffffff' }}>{stats.total_organizations}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.allOrganizations')}</div>
        </div>

        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><Monitor size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#8b5cf6' }} />{t('superAdmin.dashboard.totalScreens')}</h3>
          <div className="value" style={{ color: '#ffffff' }}>{stats.total_screens}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.screensCreatedTotal')}</div>
        </div>

        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><Activity size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#10b981' }} />{t('superAdmin.dashboard.onlineNow')}</h3>
          <div className="value" style={{ color: '#10b981' }}>{stats.total_online_devices}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.activeUnits')}</div>
        </div>

        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><Users size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#f59e0b' }} />{t('superAdmin.dashboard.totalUsers')}</h3>
          <div className="value" style={{ color: '#ffffff' }}>{stats.total_users}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.acrossAllCustomers')}</div>
        </div>

        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><UserCog size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#ef4444' }} />{t('superAdmin.dashboard.superAdmins')}</h3>
          <div className="value" style={{ color: '#ffffff' }}>{stats.super_admins}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.systemAdministrators')}</div>
        </div>

        <div className="stat-card">
          <h3 style={{ color: '#ffffff' }}><TrendingUp size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#06b6d4' }} />{t('superAdmin.dashboard.activeCustomers')}</h3>
          <div className="value" style={{ color: '#ffffff' }}>{stats.active_organizations}</div>
          <div className="label" style={{ color: '#9ca3af' }}>{t('superAdmin.dashboard.activeOrganizations')}</div>
        </div>
      </div>

      {/* Detailed Customer Statistics */}
      {detailedStats && (
        <>
          <div style={{ marginTop: '3rem', marginBottom: '1.5rem' }}>
            <h2>{t('superAdmin.dashboard.activeCustomers')}</h2>
            <p style={{ opacity: 0.7, marginTop: '0.5rem' }}>
              {t('superAdmin.dashboard.acrossAllCustomers')}
            </p>
          </div>

          <div className="card">
            <table className="data-table" style={{ color: '#ffffff' }}>
              <thead>
                <tr>
                  <th>{t('superAdmin.dashboard.organization')}</th>
                  <th>{t('superAdmin.dashboard.screens')}</th>
                  <th>{t('superAdmin.dashboard.onlineScreens')}</th>
                  <th>{t('superAdmin.dashboard.messages')}</th>
                  <th>{t('superAdmin.dashboard.articles')}</th>
                  <th>{t('superAdmin.dashboard.lastActive')}</th>
                </tr>
              </thead>
              <tbody>
                {detailedStats.most_active_customers.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', opacity: 0.6 }}>
                      {t('common.noData')}
                    </td>
                  </tr>
                ) : (
                  detailedStats.most_active_customers.map((customer, idx) => (
                    <tr key={customer.organization_id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Building2 size={16} style={{ opacity: 0.5 }} />
                          <strong>{customer.organization_name}</strong>
                        </div>
                      </td>
                      <td>
                        <span style={{ 
                          fontSize: '1.1rem', 
                          fontWeight: '600',
                          color: customer.total_screens > 0 ? '#50E3C2' : '#666'
                        }}>
                          {customer.total_screens}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span 
                            className={customer.online_screens > 0 ? 'badge badge-success' : 'badge badge-secondary'}
                            style={{ fontSize: '0.9rem' }}
                          >
                            {customer.online_screens} {t('superAdmin.dashboard.online')}
                          </span>
                        </div>
                      </td>
                      <td>{customer.total_messages}</td>
                      <td>{customer.total_articles}</td>
                      <td style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                        {customer.last_active ? (
                          new Date(customer.last_active).toLocaleString('no-NO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        ) : (
                          <span style={{ opacity: 0.5, color: 'white' }}>{t('superAdmin.dashboard.never')}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ 
            marginTop: '2rem', 
            padding: '1.5rem', 
            background: 'rgba(80, 227, 194, 0.05)', 
            borderRadius: '8px',
            border: '1px solid rgba(80, 227, 194, 0.2)'
          }}>
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={20} color="#50E3C2" />
              <span style={{ color: '#ffffff' }}>{t('superAdmin.dashboard.systemOverview')}</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.totalCustomers')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#50E3C2' }}>
                  {detailedStats.total_customers}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.totalScreens')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#50E3C2' }}>
                  {detailedStats.total_screens}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.onlineDevices')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                  {detailedStats.total_online_devices}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.averageOnlinePerCustomer')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
                  {detailedStats.total_customers > 0 
                    ? (detailedStats.total_online_devices / detailedStats.total_customers).toFixed(1)
                    : '0'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Financial Overview */}
          <div style={{
            background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid rgba(80, 227, 194, 0.2)',
            marginTop: '2rem'
          }}>
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} color="#50E3C2" />
              <span style={{ color: '#ffffff' }}>{t('superAdmin.dashboard.financialOverview')}</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.demoCustomers')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f59e0b' }}>
                  {detailedStats.demo_customers || 0}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{t('superAdmin.dashboard.nonBillable')}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.paidCustomers')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                  {detailedStats.paid_customers || 0}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{t('superAdmin.dashboard.billable')}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.paidScreens')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
                  {detailedStats.paid_screens || 0}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{t('superAdmin.dashboard.perScreenPerMonth')}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{t('superAdmin.dashboard.monthlyRevenue')}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#50E3C2' }}>
                  {new Intl.NumberFormat('nb-NO', { 
                    style: 'currency', 
                    currency: 'NOK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(detailedStats.monthly_recurring_revenue || 0)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{t('superAdmin.dashboard.onlyPaidCustomers')}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
